import {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Point,
  Polygon,
  area,
  feature,
  featureCollection,
  lineToPolygon,
  multiPolygon,
  point,
  polygon,
  polygonToLine,
} from '@turf/turf';
import round from 'lodash/round';
import { TGeometry } from 'FeatureModules/Site/types/plot.types';
import { v4 as uuid } from 'uuid';
import { GeoJSONGeometry, parse, stringify } from 'wellknown';

export const transformLatLngToArrayPosition = (polygon: { lat: number; lng: number }[]) =>
  polygon.map(c => [c.lng, c.lat]);

export const transformLatLngBoundsToLngLatBounds: (
  bounds?: number[]
) => [number, number, number, number] | undefined = bounds =>
  bounds?.length === 4 ? [bounds[1], bounds[0], bounds[3], bounds[2]] : undefined;

/**
 * Returns the calculated size in hectares rounded to 2 decimal digits
 * To calculate for the slight difference of the earth radius of the turf area function, we multiply by 0.993699583 to match the backends calculation
 */
export const calculateArea = (feature: Feature<unknown> | FeatureCollection<unknown> | Geometry) =>
  round((area(feature) * 0.993699583) / 10_000, 2);

export const radiusFromHectares = (hectares: number) => {
  const squareMeters = hectares * 10000; // 1 hectare = 10,000 m²
  const radius = Math.sqrt(squareMeters / Math.PI);
  return radius; // in meters
};

export const toMapFeature: (
  geometry: TGeometry,
  id?: string,
  plotExternalId?: string
) => Feature<Polygon | MultiPolygon | Point, { plotId?: string }> = (
  geometry,
  id = uuid(),
  plotExternalId
) => {
  switch (geometry.type) {
    case 'Point':
      return point(geometry.coordinates, plotExternalId ? { plotId: plotExternalId } : undefined, {
        id,
      });
    case 'Polygon':
      return polygon(
        geometry.coordinates,
        plotExternalId ? { plotId: plotExternalId } : undefined,
        { id }
      );
    case 'MultiPolygon':
      return multiPolygon(
        geometry.coordinates,
        plotExternalId ? { plotId: plotExternalId } : undefined,
        { id }
      );
    default:
      throw Error('Invalid geometry type');
  }
};

export const wktToFeatureCollection = (
  wktString: string
): FeatureCollection<Point | Polygon | MultiPolygon> => {
  const parsedWkt = parse(wktString);
  if (!parsedWkt) {
    throw new Error('Invalid WKT string');
  }
  if (parsedWkt.type === 'GeometryCollection') {
    const features = parsedWkt.geometries.map(geometry => feature(geometry));
    if (
      features.every(
        ({ geometry }) =>
          geometry.type === 'Point' ||
          geometry.type === 'Polygon' ||
          geometry.type === 'MultiPolygon'
      )
    ) {
      throw new Error('Supported WKT geometries are Point, Polygon, MultiPolygon');
    }
    return featureCollection(features as Feature<Point | Polygon | MultiPolygon>[]);
  }
  return featureCollection([feature(parsedWkt) as Feature<Point | Polygon | MultiPolygon>]);
};

export const featureToWkt = (
  feature: Feature<Point | Polygon | MultiPolygon> | undefined
): string | undefined => {
  if (!feature) {
    return undefined;
  }

  const wkt = stringify(feature.geometry as GeoJSONGeometry);
  if (!wkt) {
    throw new Error('Failed to convert geometry to WKT');
  }
  return wkt;
};

/**
 *  Close any open geometries in the WKT by transforming into geojson and back to wkt
 */
export const closedWktGeometriesAsFeatures = (
  wktString: string
): Feature<Point | Polygon | MultiPolygon>[] => {
  const featureCollection = wktToFeatureCollection(wktString);
  return featureCollection.features.map(feature => {
    if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      return lineToPolygon(polygonToLine(feature.geometry), { autoComplete: true });
    }
    return feature;
  });
};

/**
 *  Close any open geometries in the WKT by transforming into geojson and back to wkt
 */
export const closeOpenWktGeometries = (wktString: string): string | undefined =>
  featureToWkt(closedWktGeometriesAsFeatures(wktString)[0]);
